import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Fab,
  Snackbar,
} from "@mui/material";
import { KeyboardArrowUp as ArrowUpIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { formatDate } from "../../utils/dateFormat";
import PublicLayout from "./PublicLayout";
import log from "../../utils/logger";
import {
  HOME_PAGE_NEWS_COUNT,
  HOME_PAGE_CHECK_INTERVAL,
  HOME_PAGE_CACHE_TTL,
  HOMEPAGE_CACHE_KEY,
  MAX_AUTO_LOAD_ARTICLES,
} from "../../config/constants";

interface NewsArticle {
  id: string;
  title: string;
  subtitle: string;
  content: string;
  images?: string[];
  videoLinks?: string[];
  publishedAt: any;
}

const PublicHomepage: React.FC = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [checking, setChecking] = useState(false);

  // Infinite scroll state
  const [loadedPages, setLoadedPages] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [hasMoreToLoad, setHasMoreToLoad] = useState(true);

  // UI state
  const [showGoToTop, setShowGoToTop] = useState(false);
  const [showNewArticlesBanner, setShowNewArticlesBanner] = useState(false);
  const [newArticlesCount, setNewArticlesCount] = useState(0);

  // Cache management
  const [cachedVersion, setCachedVersion] = useState<number | null>(null);

  // Refs
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  // Load cache from sessionStorage
  const loadCacheFromStorage = () => {
    try {
      const cached = sessionStorage.getItem(HOMEPAGE_CACHE_KEY);
      if (cached) {
        const { data, version, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;

        if (age < HOME_PAGE_CACHE_TTL) {
          log.debug(`Loading cached homepage from storage (age: ${Math.round(age / 1000)}s)`);
          return { data, version };
        } else {
          log.debug("Cached homepage expired, clearing");
          sessionStorage.removeItem(HOMEPAGE_CACHE_KEY);
        }
      }
    } catch (err) {
      log.error("Failed to load cache from storage:", err);
    }
    return null;
  };

  // Save cache to sessionStorage
  const saveCacheToStorage = (data: any, version: number) => {
    try {
      sessionStorage.setItem(
        HOMEPAGE_CACHE_KEY,
        JSON.stringify({
          data,
          version,
          timestamp: Date.now(),
        })
      );
    } catch (err) {
      log.error("Failed to save cache to storage:", err);
    }
  };

  // Check for new articles (page 1 only)
  const checkForUpdates = useCallback(async () => {
    if (!cachedVersion && cachedVersion !== 0) {
      log.debug("No cached version yet, skipping update check");
      return;
    }

    try {
      setChecking(true);
      setLastChecked(new Date());

      log.debug(`Checking for homepage updates (current version: ${cachedVersion})`);
      const result = await api.getPublicHomepage(1, HOME_PAGE_NEWS_COUNT, cachedVersion);

      if (result.success && result.modified === false) {
        // No changes - 304 response
        log.debug(`Homepage unchanged: v${cachedVersion}`);
      } else if (result.success && result.modified) {
        // Version changed - content has been updated (new articles, reordered, or republished)
        log.debug(`Homepage updated: v${cachedVersion} -> v${result.version}`);

        // Don't update cachedVersion yet - this forces fresh fetch when user clicks banner
        setShowNewArticlesBanner(true);

        // Count actual new articles for the banner message
        const currentFirstPageIds = news.slice(0, HOME_PAGE_NEWS_COUNT).map((a: NewsArticle) => a.id);
        const newArticleIds = result.articles.map((a: any) => a.id);
        const newArticleCount = newArticleIds.filter((id: string) => !currentFirstPageIds.includes(id)).length;
        setNewArticlesCount(newArticleCount);

        log.debug(`Content updated: ${newArticleCount} new articles, new version: ${result.version}`);
      }
    } catch (err: any) {
      log.error("Error checking for updates:", err);
    } finally {
      setChecking(false);
    }
  }, [cachedVersion, news]);

  // Fetch homepage (can append for infinite scroll or replace for refresh)
  const fetchHomepage = async (page = 1, append = false) => {
    if (isLoadingRef.current) {
      log.debug("Already loading, skipping request");
      return;
    }

    try {
      isLoadingRef.current = true;

      if (page === 1 && !append) {
        setInitialLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);
      setLastChecked(new Date());

      const result = await api.getPublicHomepage(page, HOME_PAGE_NEWS_COUNT, cachedVersion || undefined);

      if (result.success && result.modified === false) {
        // Using cached data (304)
        log.debug("Using cached homepage (304)");
        return;
      }

      if (result.success && result.modified) {
        if (append) {
          // Append new page to existing articles
          setNews(prev => [...prev, ...result.articles]);
          setLoadedPages(page);
        } else {
          // Replace all articles (refresh scenario)
          setNews(result.articles);
          setLoadedPages(1);
        }

        setTotalArticles(result.totalCount);
        setTotalPages(result.totalPages);
        setCachedVersion(result.version || 0);
        setHasMoreToLoad(page < result.totalPages);

        // Save to cache (save all loaded articles)
        saveCacheToStorage({
          articles: append ? [...news, ...result.articles] : result.articles,
          pinnedCount: result.pinnedCount,
          totalCount: result.totalCount,
          totalPages: result.totalPages,
          loadedPages: append ? page : 1,
        }, result.version || 0);
      } else if (!result.success) {
        setError(result.error || "Failed to load news");
      }
    } catch (err: any) {
      log.error("Error fetching homepage:", err);
      setError(err.message || "Failed to load news");
    } finally {
      isLoadingRef.current = false;
      setInitialLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Load next page
  const loadNextPage = useCallback(() => {
    const nextPage = loadedPages + 1;
    if (nextPage <= totalPages && !isLoadingRef.current) {
      log.debug(`Loading page ${nextPage}`);
      fetchHomepage(nextPage, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedPages, totalPages]);

  // Handle refresh button click
  const handleRefreshClick = () => {
    setShowNewArticlesBanner(false);
    setNewArticlesCount(0);

    // Clear stale sessionStorage cache to force fresh fetch
    sessionStorage.removeItem(HOMEPAGE_CACHE_KEY);
    log.debug("Cleared stale homepage cache, fetching fresh data");

    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      // Fetch with current (old) cachedVersion to force backend to return fresh data
      fetchHomepage(1, false);
    }, 300);
  };

  // Handle manual refresh
  const handleManualRefresh = () => {
    if (!checking && !isLoadingRef.current) {
      checkForUpdates();
    }
  };

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Initial load
  useEffect(() => {
    // Try to load from sessionStorage first
    const cached = loadCacheFromStorage();

    if (cached) {
      // We have valid cached data - show it immediately
      log.debug("Using cached homepage from storage (instant load)");

      setNews(cached.data.articles || []);
      setTotalArticles(cached.data.totalCount || 0);
      setTotalPages(cached.data.totalPages || 1);
      setLoadedPages(cached.data.loadedPages || 1);
      setCachedVersion(cached.version);
      setHasMoreToLoad((cached.data.loadedPages || 1) < (cached.data.totalPages || 1));

      setInitialLoading(false);

      // Trigger a background check for updates
      setTimeout(() => checkForUpdates(), 100);
    } else {
      // No valid cache - fetch fresh data
      log.debug("No cached data, fetching fresh homepage");
      fetchHomepage(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    const currentRef = loadMoreTriggerRef.current;
    if (!currentRef || !hasMoreToLoad) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMoreToLoad && !isLoadingRef.current) {
          // Check if we're under the auto-load limit
          if (news.length < MAX_AUTO_LOAD_ARTICLES) {
            log.debug("Intersection detected, loading next page");
            loadNextPage();
          }
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMoreToLoad, news.length, loadedPages, totalPages, loadNextPage]);

  // Scroll position tracking for "Go to Top" button
  useEffect(() => {
    const handleScroll = () => {
      setShowGoToTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-check for updates every CHECK_INTERVAL when page is visible
  useEffect(() => {
    // Only start auto-refresh if we have a cached version
    if (cachedVersion === null) {
      log.debug("No cached version yet, auto-refresh not started");
      return;
    }

    log.debug(`Starting auto-refresh with interval ${HOME_PAGE_CHECK_INTERVAL}ms`);
    let intervalId: NodeJS.Timeout | null = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, clear interval
        if (intervalId) {
          log.debug("Page hidden, stopping auto-refresh");
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        // Page is visible, start interval
        if (!intervalId) {
          log.debug("Page visible, starting auto-refresh");
          intervalId = setInterval(() => {
            checkForUpdates();
          }, HOME_PAGE_CHECK_INTERVAL);
        }
      }
    };

    // Start interval if page is visible
    if (!document.hidden) {
      intervalId = setInterval(() => {
        checkForUpdates();
      }, HOME_PAGE_CHECK_INTERVAL);
    }

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalId) {
        log.debug("Cleaning up auto-refresh interval");
        clearInterval(intervalId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [cachedVersion, checkForUpdates]);

  const handleArticleClick = (articleId: string) => {
    navigate(`/news/${articleId}`);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Helper function to convert timestamp to Date
  const convertToDate = (timestamp: any): Date | null => {
    try {
      // Check if it's a Firestore Timestamp with toDate method
      if (timestamp.toDate && typeof timestamp.toDate === "function") {
        return timestamp.toDate();
      }
      // Check if it's a plain object with _seconds (from Cloud Functions)
      else if (timestamp._seconds !== undefined) {
        return new Date(timestamp._seconds * 1000);
      }
      // Check if it's already a Date object
      else if (timestamp instanceof Date) {
        return timestamp;
      }
      // Try to parse it as a number or string
      else {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? null : date;
      }
    } catch {
      return null;
    }
  };

  // Helper function to check if article was published today
  const isPublishedToday = (publishedAt: any): boolean => {
    const publishedDate = convertToDate(publishedAt);
    if (!publishedDate) return false;

    const today = new Date();
    return (
      publishedDate.getDate() === today.getDate() &&
      publishedDate.getMonth() === today.getMonth() &&
      publishedDate.getFullYear() === today.getFullYear()
    );
  };

  // Helper function to check if article was published within last 60 minutes
  const isPublishedRecently = (publishedAt: any): boolean => {
    const publishedDate = convertToDate(publishedAt);
    if (!publishedDate) return false;

    const now = new Date();
    const diffInMinutes = (now.getTime() - publishedDate.getTime()) / (1000 * 60);
    return diffInMinutes >= 0 && diffInMinutes < 60;
  };

  // Helper function to get video thumbnail URL
  const getVideoThumbnail = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      // YouTube
      if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
        let videoId = '';
        if (hostname.includes('youtu.be')) {
          videoId = urlObj.pathname.slice(1).split('/')[0];
        } else if (urlObj.pathname.includes('/embed/')) {
          videoId = urlObj.pathname.split('/embed/')[1].split('/')[0];
        } else if (urlObj.pathname.includes('/shorts/')) {
          videoId = urlObj.pathname.split('/shorts/')[1].split('/')[0];
        } else {
          videoId = urlObj.searchParams.get('v') || '';
        }
        if (videoId) {
          return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }

      // For other video platforms, return null (no thumbnail available)
      return null;
    } catch {
      return null;
    }
  };

  if (initialLoading) {
    return (
      <PublicLayout
        lastUpdated={lastChecked}
        onRefresh={handleManualRefresh}
        isRefreshing={checking || isLoadingRef.current}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout
        lastUpdated={lastChecked}
        onRefresh={handleManualRefresh}
        isRefreshing={checking || isLoadingRef.current}
      >
        <Alert severity="info">{error}</Alert>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout
      lastUpdated={lastChecked}
      onRefresh={handleManualRefresh}
      isRefreshing={checking || isLoadingRef.current}
      tickerArticles={news}
    >
      {/* New Articles Banner */}
      <Snackbar
        open={showNewArticlesBanner}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 70, sm: 80 } }}
      >
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRefreshClick}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          }
          onClose={() => setShowNewArticlesBanner(false)}
          sx={{ minWidth: 300 }}
        >
          {newArticlesCount > 0
            ? `${newArticlesCount} new article${newArticlesCount !== 1 ? 's' : ''} available`
            : 'News updated'}
        </Alert>
      </Snackbar>

      {news.length === 0 ? (
        <Alert severity="info">No news articles available at the moment.</Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {news.map((article, index) => {
              return (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Card
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleArticleClick(article.id)}
                      sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                    >
                      {(() => {
                        // Show image if available, otherwise show video thumbnail
                        if (article.images && article.images.length > 0) {
                          return (
                            <CardMedia
                              component="img"
                              height="200"
                              image={article.images[0]}
                              alt={article.title}
                              sx={{ objectFit: "cover" }}
                            />
                          );
                        } else if (article.videoLinks && article.videoLinks.length > 0) {
                          const thumbnail = getVideoThumbnail(article.videoLinks[0]);
                          if (thumbnail) {
                            return (
                              <CardMedia
                                component="img"
                                height="200"
                                image={thumbnail}
                                alt={article.title}
                                sx={{ objectFit: "cover" }}
                              />
                            );
                          }
                        }
                        return null;
                      })()}
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ mb: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {/* Featured tag - only for top 2 articles published today */}
                          {index < 2 && isPublishedToday(article.publishedAt) && (
                            <Chip
                              label="खास बातमी"
                              color="primary"
                              size="small"
                              sx={{
                                "& .MuiChip-label": {
                                  paddingTop: "1px",
                                  paddingBottom: "0px",
                                  lineHeight: 1.2,
                                },
                              }}
                            />
                          )}
                          {/* Breaking News tag - for articles published within last 60 minutes */}
                          {isPublishedRecently(article.publishedAt) && (
                            <Chip
                              label="ताज्या बातम्या"
                              size="small"
                              sx={{
                                bgcolor: "#ff6200",
                                color: "white",
                                "& .MuiChip-label": {
                                  paddingTop: "1px",
                                  paddingBottom: "0px",
                                  lineHeight: 1.2,
                                },
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ fontWeight: "bold", lineHeight: 1.3 }}
                        >
                          {article.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          paragraph
                        >
                          {truncateText(article.subtitle, 120)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(article.publishedAt)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Infinite scroll trigger (invisible div) */}
          {hasMoreToLoad && news.length < MAX_AUTO_LOAD_ARTICLES && (
            <Box ref={loadMoreTriggerRef} sx={{ height: 20, mt: 4 }} />
          )}

          {/* Loading indicator */}
          {isLoadingMore && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={4}
              mb={2}
            >
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" ml={2}>
                Loading more articles...
              </Typography>
            </Box>
          )}

          {/* Load More button (shown after MAX_AUTO_LOAD_ARTICLES) */}
          {hasMoreToLoad && news.length >= MAX_AUTO_LOAD_ARTICLES && !isLoadingMore && (
            <Box display="flex" justifyContent="center" mt={4} mb={2}>
              <Button
                variant="contained"
                size="large"
                onClick={loadNextPage}
                disabled={isLoadingRef.current}
              >
                Load More Articles
              </Button>
            </Box>
          )}

          {/* End of content message */}
          {!hasMoreToLoad && news.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 4, mb: 2 }}
            >
              You've reached the end • {totalArticles} articles total
            </Typography>
          )}

          {/* Go to Top button */}
          {showGoToTop && (
            <Fab
              color="primary"
              size="medium"
              onClick={scrollToTop}
              sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <ArrowUpIcon />
            </Fab>
          )}
        </>
      )}
    </PublicLayout>
  );
};

export default PublicHomepage;
